import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { of, Subject } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { ServicePlanReconciliationItemDto } from 'src/app/models/service-plan/service-plan-reconciliation-item-dto'
import { ServicePlanReconciliationTableColumnDefinitions } from 'src/app/models/table-defs/service-plan-reconciliation-table-def'
import { TableColumnDefinition } from 'src/app/models/table/table-column-definition'
import { TableLinkColumnDTO } from 'src/app/models/table/table-link-column-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ServicePlanReconciliationService } from 'src/app/services/service-plan/service-plan-reconciliation.service'
import { ServicePlanSubmissionService } from 'src/app/services/service-plan/service-plan-submission.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { ServicePlanBulkRetryConfirmModalComponent } from '../../modals/service-plan-bulk-retry-confirm-modal/service-plan-bulk-retry-confirm-modal.component'

// Table row shape — a dashboard deep-link for the client name (same TableLinkColumnDTO mechanism
// ClientTableDTO.preferredFullName already uses) plus one formatted display string per step
// timestamp (check/pending icon-and-text, never colour alone — pre-review-checklist.md).
// `retry` is null for a complete submission — the table renders no button for a null cell.
class ServicePlanReconciliationTableDTO {
  clientName: TableLinkColumnDTO
  dateOfBirth: string
  submittedOn: string
  pdfGeneratedOn: string
  emailSentOn: string
  pdfAttachedOn: string
  noteCreatedOn: string
  failureReason: string
  retry: TableLinkColumnDTO | null
}

const DEFAULT_WINDOW_DAYS = 30
const DATE_FORMAT = 'Do MMMM, YYYY'
const DATE_TIME_FORMAT = 'Do MMMM, YYYY h:mm a'

@Component({
  selector: 'app-reconciliation-view',
  templateUrl: './reconciliation-view.component.html',
  styleUrls: ['./reconciliation-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReconciliationViewComponent implements OnInit, OnDestroy {
  _tableColumnsToDisplay: string[] = [
    'clientName',
    'dateOfBirth',
    'submittedOn',
    'pdfGeneratedOn',
    'emailSentOn',
    'pdfAttachedOn',
    'noteCreatedOn',
    'failureReason',
    'retry',
  ]
  _tableColumnDefinitions: TableColumnDefinition[] =
    ServicePlanReconciliationTableColumnDefinitions
  _tableData: MatTableDataSource<ServicePlanReconciliationTableDTO> =
    new MatTableDataSource<ServicePlanReconciliationTableDTO>()
  _incompleteOnly = false
  _dataLoaded = false
  _incompleteCount = 0
  // True from the moment a retry (single or bulk) is confirmed until it settles — guards against a
  // double-click queuing the same record(s) twice, and disables the bulk button meanwhile.
  _retryInProgress = false

  // A Subject + switchMap (Review Finding, e10-1 code review) rather than a plain re-invoked
  // subscribe — switchMap cancels any still-in-flight GetReconciliation call when the toggle
  // changes again, so a stale response can't overwrite a later one or mis-fire the spinner.
  private incompleteOnlyChange$ = new Subject<boolean>()
  private destroy$ = new Subject<void>()

  constructor(
    private reconciliationService: ServicePlanReconciliationService,
    private submissionService: ServicePlanSubmissionService,
    private spinnerService: SpinnerService,
    private bannerService: BannerService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.incompleteOnlyChange$
      .pipe(
        switchMap((incompleteOnly) => {
          this.spinnerService.show()
          return this.reconciliationService
            .GetReconciliation(DEFAULT_WINDOW_DAYS, incompleteOnly)
            .pipe(
              map((items) => ({ items })),
              // Caught per-request, inside the inner pipe (Review Finding, e10-1 code review) —
              // an error here must not propagate and kill the outer switchMap subscription,
              // which would otherwise silently stop the toggle from ever re-querying again.
              catchError(() => of({ error: true as const })),
            )
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if ('error' in result) {
          this.spinnerService.hide()
          this.bannerService.showBanner(
            this.translate.instant('SERVICE_PLAN_RECONCILIATION.LOAD_ERROR'),
            'error',
            30000,
          )
          this.cd.markForCheck()
          return
        }

        this._tableData = this.mapReconciliationData(result.items)
        this._incompleteCount = result.items.filter((i) => i.isIncomplete).length
        this._dataLoaded = true
        this.spinnerService.hide()
        this.cd.markForCheck()
      })

    this.incompleteOnlyChange$.next(this._incompleteOnly)
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onIncompleteOnlyChange(incompleteOnly: boolean) {
    this._incompleteOnly = incompleteOnly
    this.incompleteOnlyChange$.next(incompleteOnly)
  }

  // app-table's onLinkButtonClick emits [columnName, linkData]; linkData is the submission ID.
  onRetryClick(event: string[]) {
    if (this._retryInProgress) {
      return
    }

    this._retryInProgress = true
    this.spinnerService.show()
    this.cd.markForCheck()

    this.submissionService
      .Retry(event[1])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.bannerService.showBanner(
            this.translate.instant('SERVICE_PLAN_RECONCILIATION.RETRY_QUEUED'),
            'success',
            5000,
          )
        },
        error: () => {
          this.spinnerService.hide()
          this._retryInProgress = false
          this.bannerService.showBanner(
            this.translate.instant('SERVICE_PLAN_RECONCILIATION.RETRY_ERROR'),
            'error',
            30000,
          )
          this.cd.markForCheck()
        },
        complete: () => this.finishRetry(),
      })
  }

  onRetryAllIncomplete() {
    if (this._retryInProgress || this._incompleteCount === 0) {
      return
    }

    this.dialog
      .open(ServicePlanBulkRetryConfirmModalComponent, {
        width: '605px',
        panelClass: 'are-you-sure-panel',
        ariaLabelledBy: 'bulk-retry-confirm-title',
        data: { count: this._incompleteCount },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((confirmed) => confirmed === true),
        tap(() => {
          this._retryInProgress = true
          this.spinnerService.show()
          this.cd.markForCheck()
        }),
        switchMap(() =>
          this.reconciliationService.RetryIncomplete(DEFAULT_WINDOW_DAYS),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          const key =
            result.queued === 0
              ? 'SERVICE_PLAN_RECONCILIATION.BULK_RETRY_NOTHING_TO_RETRY'
              : result.remaining > 0
                ? 'SERVICE_PLAN_RECONCILIATION.BULK_RETRY_QUEUED_PARTIAL'
                : 'SERVICE_PLAN_RECONCILIATION.BULK_RETRY_QUEUED'
          this.bannerService.showBanner(
            this.translate.instant(key, {
              queued: result.queued,
              remaining: result.remaining,
            }),
            'success',
            10000,
          )
        },
        error: () => {
          this.spinnerService.hide()
          this._retryInProgress = false
          this.bannerService.showBanner(
            this.translate.instant(
              'SERVICE_PLAN_RECONCILIATION.BULK_RETRY_ERROR',
            ),
            'error',
            30000,
          )
          this.cd.markForCheck()
        },
        // Also fires when the dialog was cancelled (filter drops the emission) — nothing to
        // settle in that case, as _retryInProgress was never set.
        complete: () => this.finishRetry(),
      })
  }

  // Runs only on a successful retry: hide the spinner, release the in-flight guard, then re-query
  // so the table reflects any step the pipeline has already completed.
  private finishRetry() {
    if (!this._retryInProgress) {
      return
    }

    this.spinnerService.hide()
    this._retryInProgress = false
    this.cd.markForCheck()
    this.incompleteOnlyChange$.next(this._incompleteOnly)
  }

  private mapReconciliationData(
    items: ServicePlanReconciliationItemDto[],
  ): MatTableDataSource<ServicePlanReconciliationTableDTO> {
    const tableData = items.map((item) => {
      const target = new ServicePlanReconciliationTableDTO()

      target.clientName = new TableLinkColumnDTO()
      target.clientName.linkText =
        // Falsy check (Review Finding, e10-1 code review) — ?? only catches null/undefined, not
        // an empty string, which would otherwise render a blank, unlabeled link.
        item.clientPreferredFullName ||
        this.translate.instant('SERVICE_PLAN_RECONCILIATION.UNKNOWN_CLIENT')
      target.clientName.linkUrl = '/client-dashboard/' + item.clientId
      target.clientName.linkData = item.clientId
      target.clientName.selected = false

      // UTC (Review Finding, e10-1 code review) — DOB is a date-only value serialized as a UTC
      // DateTime; parsing it in the browser's local zone can shift the displayed calendar day.
      target.dateOfBirth = item.clientDateOfBirth
        ? moment.utc(item.clientDateOfBirth).format(DATE_FORMAT)
        : ''
      target.submittedOn = moment(item.submittedOn).format(DATE_TIME_FORMAT)
      target.pdfGeneratedOn = this.formatStepStatus(item.pdfGeneratedOn)
      target.emailSentOn = this.formatStepStatus(item.emailSentOn)
      target.pdfAttachedOn = this.formatStepStatus(item.pdfAttachedOn)
      target.noteCreatedOn = this.formatStepStatus(item.noteCreatedOn)
      target.failureReason = item.failureReason ?? ''

      if (item.isIncomplete) {
        target.retry = new TableLinkColumnDTO()
        target.retry.linkText = this.translate.instant(
          'SERVICE_PLAN_RECONCILIATION.RETRY',
        )
        // Visible text is "Retry" on every row, so give each button a distinct accessible name.
        // Names the client already shown in the adjacent cell — no new PII surface.
        target.retry.ariaLabel = this.translate.instant(
          'SERVICE_PLAN_RECONCILIATION.RETRY_ARIA_LABEL',
          { client: target.clientName.linkText },
        )
        target.retry.linkData = item.submissionId
        target.retry.selected = false
      } else {
        target.retry = null
      }

      return target
    })

    return new MatTableDataSource<ServicePlanReconciliationTableDTO>(tableData)
  }

  private formatStepStatus(timestamp: string | null): string {
    // Icon lives in the translation value itself (Review Finding, e10-1 code review), not
    // concatenated in code, so translators own the full label rather than an inconsistently
    // decorated plain string.
    return timestamp
      ? this.translate.instant('SERVICE_PLAN_RECONCILIATION.STEP_COMPLETE')
      : this.translate.instant('SERVICE_PLAN_RECONCILIATION.STEP_PENDING')
  }
}
